/* eslint-env es6 */
/* eslint-disable no-restricted-syntax */

// Please see https://github.com/aops-ba/trove-shared/blob/main/packages/crow/README.md

/* global optimizely */
window["optimizely"] = window["optimizely"] || [];

class CRO {
	/**
	 * Check if Optimizely has loaded or not.
	 * Failure could be caused by some adblockers like uBlock Origin.
	 */
	checkForSuccessfulLoad() {
		if (Array.isArray(window["optimizely"])) {
			// If it's an Array, then it didn't load.
			return false;
		}
		return true;
	}

	/**
	 * [Official documentation](https://docs.developers.optimizely.com/web-experimentation/reference/event)
	 *
	 * @param name {string} The API name of the event
	 * @param value {float} Not necessarily a dollar amount
	 * @param tags Additional data to send with the event
	 * @param revenue {number} The amount of revenue from the conversion in
	 * 		dollars; each product calculates revenue differently
	 * 		([see explaination](https://docs.google.com/document/d/1JraaYEqe4tV9vscnan60DBLSCg-o1N8_RO1wDqDts2k/edit#heading=h.73dosbglyion)).
	 *      Generally, the revenue is the subtotal of the transaction without
	 *      tax or shipping.
	 */
	sendEvent(name, value = 0, tags = {}, revenue = 0) {
		if (!this.checkForSuccessfulLoad()) {
			return;
		}

		let eventData = {};
		if (revenue) {
			eventData.revenue = Math.round(revenue * 100);
		}
		if (value) {
			eventData.value = value;
		}
		eventData = {...eventData, ...tags};

		optimizely.push({
			type: "event",
			eventName: name,
			tags: eventData,
		});
	}

	loud() {
		if (!this.checkForSuccessfulLoad()) {
			return;
		}

		optimizely.push({
			type: "log",
			level: "DEBUG",
		});
	}

	forceSync() {
		if (!this.checkForSuccessfulLoad()) {
			return;
		}

		optimizely.push({
			type: "sendEvents",
		});
	}

	disable() {
		if (!this.checkForSuccessfulLoad()) {
			return;
		}

		optimizely.push({
			type: "optOut",
			isOptOut: true,
		});
	}

	enable() {
		if (!this.checkForSuccessfulLoad()) {
			return;
		}

		optimizely.push({
			type: "optOut",
			isOptOut: false,
		});
	}

	reload() {
		if (!this.checkForSuccessfulLoad()) {
			return;
		}

		optimizely.push({
			type: "activate",
		});
	}

	/**
	 * The next few CRO functions are a modified version of
	 * https://github.com/thegreekjester/cross_origin_solutions/blob/master/link_decoration.js
	 */

	/**
	 * Looks for a URL parameter with a croid. If its there, this user is coming from another AoPS domain.
	 * This will take that parameter and replace the optimizelyEndUserId cookie with that value, so Optimizely
	 * sees them as the same user.
	 *
	 * @return {string} The CRO ID of the user
	 */
	handleIncomingCROUser() {
		// Provide top level / sub domain changes
		var CROId =
			optimizely && !Array.isArray(window["optimizely"])
				? optimizely.get("visitor").visitorId
				: this.getCROCookie("optimizelyEndUserId");

		// If a user came from another AoPS domain, we've passed their optimizelyEndUserId in this URL parameter
		if (window.location.href.indexOf("croid") !== -1) {
			var queryCROId = this.getCROUrlParameter();
			if (queryCROId !== CROId) {
				this.setCROCookie(queryCROId);
				CROId = queryCROId;
			}
		}

		return CROId;
	}

	/**
	 * Modifies all links to other AoPS sites to include the CRO ID of the user. The other side will pick this up
	 * and make sure Optimizely seems them as the same user.
	 *
	 * @param {string} CROId The CRO ID to add to the links
	 */
	addCROToLinks(CROId) {
		if (!CROId) {
			return;
		}

		function getDomainName(url) {
			try {
				let parsedUrl = new URL(url);
				return parsedUrl.hostname;
			} catch (e) {
				console.error("Invalid URL:", e);
				return null;
			}
		}

		// Now add the CRO ID to all links to our domains
		var domains = [
			getDomainName(AoPS.aops_url),
			getDomainName(AoPS.academy_url),
			getDomainName(AoPS.beast_url),
		];

		domains.forEach((domain) => {
			// Find all links that contain the external domain
			var linksContainingDomain = document.querySelectorAll(
				"a[href*='" + domain + "'"
			);

			// Add the CRO ID to the URL
			linksContainingDomain.forEach((tag) => {
				tag.href += (tag.href.split("?")[1] ? "&" : "?") + "croid=" + CROId;
			});
		});
	}

	/**
	 * Helper function to get the optimizelyEndUserId cookie value
	 *
	 * @return {string} The CRO ID of the user
	 */
	getCROCookie() {
		var match = document.cookie.match("optimizelyEndUserId=([^;]*)");
		return match ? match[1] : undefined;
	}

	/**
	 * Helper function to set the optimizelyEndUserId cookie value
	 *
	 * @param {string} value The value to set the cookie to
	 */
	setCROCookie(value) {
		// Their cookie is set at the top level domain
		var domain = window.location.hostname.split(".").slice(-2).join(".");

		var d = new Date();
		d.setTime(d.getTime() + 24 * 60 * 60 * 1000 * 180); // 6 months

		document.cookie =
			"optimizelyEndUserId=" +
			value +
			";domain=" +
			domain +
			";path=/;expires=" +
			d.toGMTString();
	}

	/**
	 * Finds the croid URL parameter if it exists.
	 *
	 * @return {string} The CRO ID of the user, coming from the URL parameter
	 */
	getCROUrlParameter() {
		var pageUrl = window.location.search.substring(1),
			urlParams = pageUrl.split("&"),
			param,
			i;

		for (i = 0; i < urlParams.length; i++) {
			param = urlParams[i].split("=");

			if (param[0] === "croid") {
				return param[1] === undefined ? true : decodeURIComponent(param[1]);
			}
		}
	}

	/**
	 * Wait until the passed function returns `true`.
	 *
	 * @param func {function} A function which returns a boolean
	 * @return {Promise} Resolves once `func` returns true; catches errors
	 */
	until(func) {
		if (!this.checkForSuccessfulLoad()) {
			return;
		}

		const utils = optimizely.get("utils");

		return utils.waitUntil(func).catch((error) => {
			AoPS.ErrorUtils.log("E_OPTIMIZELY_UNTIL", error);
		});
	}
}

AoPS.CRO = new CRO();

// Handles cross-domain transfering of CRO IDs
window.addEventListener("load", function () {
	var userCROID = AoPS.CRO.handleIncomingCROUser();
	AoPS.CRO.addCROToLinks(userCROID);
});
